export enum ProspectActivityType {
  AGENCY_CAPTURE_FORM = 'AGENCY_CAPTURE_FORM',
  APPOINTMENT_AT_HOME = 'APPOINTMENT_AT_HOME',
  APPOINTMENT_REQUEST = 'APPOINTMENT_REQUEST',
  EVENT_LEAD_CAPTURE = 'EVENT_LEAD_CAPTURE',
  HQ_CAPTURE_FORM = 'HQ_CAPTURE_FORM',
  LINKEDIN_LEAD_CAPTURE = 'LINKEDIN_LEAD_CAPTURE',
  META_LEAD_CAPTURE = 'META_LEAD_CAPTURE',
  NEWSLETTER_SUBSCRIBE_FORM = 'NEWSLETTER_SUBSCRIBE_FORM',
  TEST_DRIVE_FORM = 'TEST_DRIVE_FORM',
  SHARE_DESIGN_FORM = 'SHARE_DESIGN_FORM',
  CONTACT_US = 'CONTACT_US',
  GENERAL_CONTACT_FORM = 'GENERAL_CONTACT_FORM',
  LOCATION_CHANGED = 'LOCATION_CHANGED',
}

export type ProspectAddressDetail = {
  addressLine1?: string;
  addressLine2?: string;
  stateProvince?: string;
  additionalProp1?: Record<string, unknown>;
  additionalProp2?: Record<string, unknown>;
  additionalProp3?: Record<string, unknown>;
};

export type ProspectAssignmentLog = {
  prospectAssignmentLogIdentifier?: string;
  prospectIdentifier?: string;
  fromBusinessPartner?: string;
  toBusinessPartner?: string;
  changedBy?: string;
  changedByUserIdentifier?: string;
  comments?: string;
  startDatetime?: string;
  endDatetime?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
  businessPartnerLocation?: string;
};

export type ProspectScore = {
  prospectScoreIdentifier?: string;
  scoreValue?: number;
  startDatetime?: string;
  endDatetime?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
};

export type ProspectStatusLog = {
  prospectStatusLogIdentifier?: string;
  statusName?: string;
  subStatusName?: string;
  primaryReasonName?: string;
  secondaryReasonName?: string;
  statusNotes?: string;
  startDatetime?: string;
  endDatetime?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
};

export type TestDriveSchedule = {
  testDriveScheduleId?: string;
  sfdcSalesEventId?: string;
  businessPartnerUserId?: string;
  businessPartnerOrganizationId?: string;
  /** Identifies the specific vehicle/slot pairing, taken from the chosen slot. */
  eventConfigId?: string;
  vin?: string;
  testDriveStatus?: string;
  /** `STANDARD` or `EXTENDED`; an extended drive runs from the start datetime to the end datetime. */
  testDriveType?: string;
  scheduledStartDatetime?: string;
  scheduledEndDatetime?: string;
  appointmentLocationName?: string;
  make?: string;
  model?: string;
  trim?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
};

export type CheckInAgreementRequest = {
  preferredLocale: string;
};

export type CheckInAgreementResponse = {
  salesEventId: string;
  viewUrl: string;
  preferredLocale: string;
  drivingType: 'Supervised';
};

export type ProspectContactTracking = {
  prospectContactTrackingId?: string;
  contactedType?: string;
  contactedMethod?: string;
  lastContacted?: string;
  nextToBeContacted?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
};

export type ProspectActivity = {
  prospectActivityIdentifier?: string;
  prospectIdentifier?: string;
  sfdcEventIdentifier?: string;
  activityType?: ProspectActivityType;
  activityTypeName?: string;
  activityDescription?: string;
  activityTimestamp?: string;
  activityReadTimestamp?: string | null;
  readByUserIdentifier?: string | null;
  activitySource?: string;
  lastSyncedToSfdcAt?: string;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;
  activityDetail?: ProspectActivityDetails | null;
};

export type ProspectActivityDetails = {
  status?: string | null;
  vehicle?: string | null;
  interestedTrim?: string | null;
  location?: string | null;
  shortCode?: string | null;
  appointmentDateTime?: string | null;
  scheduledStartDateTime?: string | null;
  /** Set on test drive history entries when the booking time was changed. */
  previousScheduledStartDateTime?: string | null;
  /** Set on test drive history entries when the booking location was changed. */
  previousLocation?: string | null;
  comments?: string | null;
  captureForm?: string | null;
  type?: string | null;
};

export type ProspectPurchaseProfile = {
  decisionMakerNames?: string;
  preferredContactMethod?: string;
  interestedModel?: string;
  interestedVehicleCondition?: string;
  currentCarMake?: string;
  currentCarModel?: string;
  isEvOwnership?: boolean;
  objections?: string;
  importantFeatures?: string;
};

export type ProspectAddress = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  zipCode?: string;
  countryCode?: string;
  region?: string;
  dma?: string;
  createdDatetime?: string;
  createdBy?: string;
  isActive?: boolean;
  updatedDatetime?: string;
};

export type ProspectKeyFields = {
  nextSteps?: string;
};

export type ProspectLocation = {
  locationIdentifier?: string;
  locationName?: string;
};

export type NoteCreatedBy = {
  userId?: string;
  fullName?: string;
};

export type Note = {
  noteIdentifier?: string;
  entityType?: string;
  entityIdentifier?: string;
  noteContent?: string;
  createdBy?: NoteCreatedBy;
  createdAt?: string;
};

export type ProspectFileMetadata = {
  createdAt: string;
  fileId?: string;
  tenantId?: string;
  customerId: string;
  category: string;
  moduleName: string;
  moduleReferenceIdentifier: string;
  fileNameOriginal: string;
  fileNameSanitized: string;
  mimeType: string;
};

export type Prospect = {
  prospectIdentifier: string;
  sfdcProspectIdentifier?: string;
  sfdcLeadIdentifier?: string;
  sfdcCustomerRecordNumber?: string;
  prospectFirstName: string;
  prospectMiddleName?: string;
  prospectLastName: string;
  prospectTitle?: string;
  prospectEmail: string;
  prospectPhone?: string;
  prospectCompanyName?: string;
  sourceName?: string;
  prospectSourceName?: string;
  sourceEventType?: string;
  prospectCaptureForm?: string;
  prospectRecordOwner?: string;
  prospectOwnerLocation?: string;
  prospectCapturedByUser?: string;
  prospectCycleMarket?: string;
  recordType?: string;
  isExistingLucidCustomer?: boolean | null;
  isOpenProspectElsewhereInNetwork?: boolean | null;
  interestedModel?: string;
  interestedTrim?: string;
  interestedVehicleCondition?: string;
  purchaseType?: string;
  purchaseTiming?: string;
  preferredContactMethod?: string;
  financeType?: string;
  currentCarMake?: string;
  isEmailOptIn?: boolean | null;
  isEmailOptOut?: boolean;
  isEmailDoubleOptIn?: boolean;
  isCallOptIn?: boolean | null;
  isCallOptOut?: boolean;
  isPrivacyConsent?: boolean;
  prospectAddressDetail?: ProspectAddressDetail;
  city?: string;
  region?: string;
  zipCode?: string;
  countryCode?: string;
  stateProvince?: string;
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isDeleted?: boolean;
  isActive?: boolean;
  noteText?: string;
  situationalNotes?: string;
  objections?: string;
  importantFeatures?: string;
  preferredLanguageIso?: string;
  status?: string;
  statusName?: string;
  notes?: Note[];
  prospectFilesMetadata?: ProspectFileMetadata[];
  prospectAssignmentLogs?: ProspectAssignmentLog[];
  prospectScores?: ProspectScore[];
  prospectStatusLogs?: ProspectStatusLog[];
  testDriveSchedules?: TestDriveSchedule[];
  prospectContactTrackings?: ProspectContactTracking[];
  purchaseProfile?: ProspectPurchaseProfile[];
  keyFields?: ProspectKeyFields;
  address?: ProspectAddress[];
  locations?: ProspectLocation[];
  /** The prospect's location; the only location field the update API reads. */
  businessPartnerOrganizationLocationIdentifier?: string;
  assignedToBusinessPartner?: string;
  assignedOwner?: string;
  prospectType?: string;
  assignedBusinessPartnerType?: string;
  assignedBy?: string;
  assignedDatetime?: string;
  assignmentComments?: string;
  comments?: string;
  nextEngagementDate?: string;
  // Datetime fields - API returns both formats
  createdDatetime?: string;
  updatedDatetime?: string;
  createdDate?: string;
  updatedAt?: string;
  captureForm?: string;
  createdBy?: string;
  updatedBy?: string;
  isTestDrive?: boolean;
  prospectActivity?: ProspectActivity;
  prospectActivities?: ProspectActivity[];
  prospectActivityDetail?: ProspectActivityDetails | null;
};

export type QuoteListItem = {
  quote_identifier: string;
  model: string;
  trim: string;
  vin: string;
  vin_status: string | null;
  total_amount: string | null;
  final_subtotal_amount: string | null;
  preferredPaymentMethod: string;
  expiry_date: string | null;
  status: string;
};

export type ProspectDetailsResponse = {
  prospectDetails: Prospect;
  quoteList: QuoteListItem[];
};

export type ProspectExportHistoryResponse = {
  items: Array<{
    fileId: string;
    fileNameOriginal?: string;
    createdBy?: string;
    createdAt: string;
  }>;
};

export type CreateProspectRequest = {
  // Consent fields
  stateProvince?: string;
  isEmailOptIn?: boolean | null;
  isCallOptIn?: boolean | null;
  isCallOptOut?: boolean | null;
  isPrivacyConsent?: boolean;

  // Address fields
  prospectAddressDetail?: ProspectAddressDetail;
  city?: string;
  region?: string;
  zipCode?: string;
  countryCode?: string;

  // Finance and vehicle fields
  financeType?: string;
  currentCarMake?: string;

  // Sync and metadata
  lastSyncedToSalesforce?: string;
  timezone?: string;
  isDeleted?: boolean;
  noteText?: string;

  // Nested arrays
  prospectAssignmentLogs?: ProspectAssignmentLog[];
  prospectScores?: ProspectScore[];
  prospectStatusLogs?: ProspectStatusLog[];
  testDriveSchedules?: TestDriveSchedule[];
  prospectContactTrackings?: ProspectContactTracking[];

  // Assignment fields
  assignedToBusinessPartner?: string;
  prospectType?: string;
  assignedBusinessPartnerType?: string;
  assignedBy?: string;
  assignedDatetime?: string;
  assignmentComments?: string;
  comments?: string;

  // Prospect identifiers
  prospectIdentifier?: string;
  sfdcProspectIdentifier?: string;
  sfdcLeadIdentifier?: string;
  sfdcCustomerRecordNumber?: string;

  // Prospect personal info
  prospectFirstName: string;
  prospectMiddleName?: string;
  prospectLastName: string;
  prospectTitle?: string;
  prospectEmail: string;
  prospectPhone?: string;
  prospectCompanyName?: string;

  // Source fields
  sourceName?: string;
  prospectSourceName?: string;
  prospectCaptureForm?: string;
  prospectRecordOwner?: string;
  prospectOwnerLocation?: string;
  prospectCapturedByUser?: string;
  prospectCycleMarket?: string;
  recordType?: string;

  // Interest fields
  interestedModel?: string;
  interestedTrim?: string;
  interestedVehicleCondition?: string;
  purchaseType?: string;
  purchaseTiming?: string;
  preferredContactMethod?: string;

  // Consent fields (from form)
  emailConsent?: string;
  callConsent?: string;

  // Audit fields
  createdDatetime?: string;
  updatedDatetime?: string;
  createdBy?: string;
  updatedBy?: string;

  // Status
  statusName?: string;

  // Test Drive flag
  isTestDrive?: boolean;
};

export type CreateProspectResponse = {
  prospectIdentifier?: string;
  success?: boolean;
  message?: string;
};

export type UpdateProspectRequest = CreateProspectRequest & {
  prospectIdentifier: string;
};

export type UpdateProspectResponse = {
  prospectIdentifier?: string;
  success?: boolean;
  message?: string;
};

export type UpdateProspectStatusRequest = {
  prospectIdentifier: string;
  status: string;
  subStatus?: string;
  primaryCloseReason?: string;
  secondaryCloseReason?: string;
  nextEngagementDate?: string;
  notes?: string;
};

export type UpdateProspectStatusResponse = {
  prospectIdentifier?: string;
  success?: boolean;
  message?: string;
};

/** Additional filters supported by POST /prospects/searches (and the export variant) — see AUCTUS-5304. */
export type ProspectSearchFilters = {
  statusNames?: string[];
  locationIds?: string[];
  assignedOwner?: string;
  sourceNames?: string[];
  /** Inclusive lower bound on createdDatetime (ISO-8601 OffsetDateTime) */
  createdFrom?: string;
  /** Inclusive upper bound on createdDatetime (ISO-8601 OffsetDateTime) */
  createdTo?: string;
};

export type UseGetProspectsParams = {
  page?: number;
  size?: number;
  searchString?: string;
  sortField?: string;
  sortOrder?: string;
  enabled?: boolean;
} & ProspectSearchFilters;

/*
|--------------------------------------------------------------------------
| LIST METADATA (GET /list-metadata?module=PROSPECT)
|--------------------------------------------------------------------------
*/

export type ListMetadataOption = {
  id: string;
  code: string;
  value: string;
  description?: string;
};

export type ListMetadataLocation = {
  locationIdentifier: string;
  organizationIdentifier?: string;
  locationType?: string;
  locationName: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
  sfdcLocationId?: string;
  isPrimary?: boolean;
};

export type ProspectListMetadataResponse = {
  prospectsource: ListMetadataOption[];
  prospectstatus: ListMetadataOption[];
  locations: ListMetadataLocation[];
};

/*
|--------------------------------------------------------------------------
| PROSPECT METRICS
|--------------------------------------------------------------------------
*/

export type ProspectStatus =
  | 'Open'
  | 'Contacting'
  | 'Engaged'
  | 'Quote Generated'
  | 'Quote Accepted'
  | 'Ordered'
  | 'Closed';

export type ProspectMetricsTimelineItem = {
  period: string;
  label: string;
  total: number;
  closed: number;
};

export type ProspectMetricsStatusItem = {
  status: ProspectStatus;
  count: number;
};

export type ProspectMetricsTotals = {
  total: number;
  closed: number;
  conversionRate: number;
  /** BRD 3.1 Performance Overview fields */
  newProspects: number;
  quotesCreated: number;
  quotesAccepted: number;
  ordersCount: number;
  deliveriesCount: number;
  testDrivesCompleted: number;
  avgOrderToDeliveryDays: number;
};

export type ProspectMetricsFunnelStage = {
  stage: string;
  count: number;
};

export type ProspectMetricsChannelItem = {
  channel: string;
  count: number;
};

export type ProspectMetricsResponse = {
  timeline: ProspectMetricsTimelineItem[];
  statusBreakdown: ProspectMetricsStatusItem[];
  totals: ProspectMetricsTotals;
  conversionFunnel: ProspectMetricsFunnelStage[];
  orderChannelBreakdown: ProspectMetricsChannelItem[];
};

export type UseGetProspectMetricsParams = {
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month';
  enabled?: boolean;
};

export type UploadProspectDocumentRequest = {
  fileId: string;
  tenantId: string;
  customerId: string;
  moduleName: string;
  moduleReferenceIdentifier: string;
  category: string;
  fileNameOriginal: string;
  fileNameSanitized: string;
  mimeType: string;
  createdAt: string;
};

export type UploadProspectDocumentResponse = {
  success: boolean;
  message?: string;
  fileId?: string;
};

export type ProspectExportSearchRequest = {
  searchString?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
} & ProspectSearchFilters;

export type ExportProspectsRequest = {
  exportType: 'ALL' | 'INCREMENTAL' | 'SEARCH';
  prospectSearchRequest?: ProspectExportSearchRequest;
};

export type ExportProspectsResponse = {
  fileId?: string;
  fileNameOriginal?: string;
  mimeType?: string;
  // Present when no prospects match the export criteria (e.g. code "204")
  status?: string;
  code?: string;
  message?: string;
  timestamp?: string;
};
